<template>
  <div class="widget-base-input" :data-prefix="!!prefix" :data-postfix="!!postfix">
    <label
      v-if="label && !checkTheme('toyota')"
      :for="name"
      :data-error="!!error"
      :data-subtitle="!!subtitle"
      :data-active="isInputFocused || !!inputValue"
      class="widget-base-input_label"
      :class="componentStyles?.inputLabel"
      @click="() => input?.focus()"
    >
      {{ label }}
    </label>
    <div class="relative" :class="{ 'mt-1': label }">
      <div v-if="prefix" class="widget-base-input_prefix">
        <WidgetIcon :icon="prefix" height="1.5rem"></WidgetIcon>
      </div>
      <input
        ref="input"
        v-model="inputValue"
        :class="
          twMerge(
            inputVariants({ variant }),
            String($attrs.class || ''),
            prefix ? 'pl-10' : '',
            clearable ? 'pr-10' : '',
            ['radio', 'checkbox'].includes(type) ? 'cursor-pointer' : 'cursor-auto'
          )
        "
        :placeholder="checkTheme('toyota') ? label : placeholder"
        :name
        :type
        :autofocus
        :autocomplete
        :readonly
        :data-error="!!error"
        :data-label="!!label"
        v-bind="$attrs"
        @focus="() => (isInputFocused = true)"
        @blur="() => (isInputFocused = false)"
      />
      <div
        v-if="clearable && inputValue"
        class="widget-base-input_postfix !pointer-events-auto cursor-pointer"
        @click="clearInput"
      >
        <WidgetIcon :icon="themeIcons.close" height="24" class="widget-secondary-icon" />
      </div>
      <div v-else-if="!!postfix" class="widget-base-input_postfix">
        <WidgetIcon :icon="postfix" height="1.5rem" class="widget-action-icon" />
      </div>
    </div>
    <div v-if="error" class="mt-1 grid grid-flow-col justify-start gap-2">
      <WidgetIcon
        v-if="
          !checkTheme(
            'volvo',
            'tesla',
            'aa',
            'suzuki',
            'cinch',
            'crashdaddy',
            'volkswagen',
            'bentley',
            'vwg',
            'samsung',
            'audi'
          )
        "
        :icon="themeIcons.closeCircle"
        height="1.25rem"
        width="1.25rem"
        class="mt-0.5 text-negative"
      ></WidgetIcon>
      <p class="font-normal text-negative">{{ error }}</p>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T extends string | boolean | number | undefined">
  import WidgetIcon from './WidgetIcon.vue';
  import { cva, type VariantProps } from 'class-variance-authority';
  import { twMerge } from 'tailwind-merge';
  import { themeIcons, checkTheme, getClientTheme, getComponentStyle } from 'src/utils/config';
  import { computed, ref } from 'vue';

  defineOptions({
    inheritAttrs: false,
  });

  const componentStyles = computed(() => getComponentStyle());
  const inputVariants = cva(
    'body1 bg-background text-fontPrimary w-full p-3 border border-backgroundSoft-600 rounded-custom block disabled:opacity-50 disabled:cursor-not-allowed focus:border-primary focus:ring-primary focus:ring-offset-0 focus:read-only:ring-0 focus-visible:shadow-none focus-visible:outline-none data-[error=true]:border-negative data-[error=true]:focus:ring-negative data-[error=true]:focus:border-negative',
    {
      variants: {
        variant: {
          default: 'text-sm focus:ring-0',
          audi: 'px-0 pt-0 border-0 border-b border-backgroundSoft focus:border-fontPrimary focus:ring-none focus:ring-0 focus:!shadow-[0px_1px_0px_0px] focus:shadow-fontPrimary data-[error=true]:focus:!shadow-negative',
          samsung:
            'px-0 pt-0 border-0 border-b border-backgroundSoft focus:border-fontPrimary focus:ring-none focus:ring-0 focus:!shadow-[0px_1px_0px_0px] focus:shadow-fontPrimary data-[error=true]:focus:!shadow-negative',
          volkswagen:
            'px-0 pt-0 border-0 border-b border-backgroundSoft focus:ring-0 data-[error=true]:focus:border-backgroundSoft',
          bentley:
            'px-0 pt-0 border-0 border-b border-backgroundSoft focus:ring-0 data-[error=true]:focus:border-backgroundSoft',
          vwg: 'px-0 pt-0 border-0 border-b border-backgroundSoft focus:ring-0 data-[error=true]:focus:border-backgroundSoft',
          mercedes:
            'bg-backgroundSoft text-sm px-4 py-3 focus:border-fontPrimary focus:ring-0 data-[label=true]:pb-1 data-[label=true]:pt-5',
          tesla:
            'bg-backgroundSoft text-sm px-4 py-3 outline-0 focus:outline-2 focus-visible:outline-2 outline-backgroundSoft-700 focus:outline-backgroundSoft-700 focus-visible:outline-backgroundSoft-700 transition-all focus:ring-none focus:ring-0 outline-offset-0 focus:outline-offset-0 focus-visible:outline-offset-0',
          aa: 'shadow-[0px_1px_4px_0px_#00000029,0px_0px_2px_0px_#00000014] focus-within:shadow-none focus-within:bg-[#fffcf2]',
          suzuki:
            'bg-backgroundSoft text-sm p-3 ring-0 focus:ring-0 border border-fontPrimary focus:border-fontPrimary',
          cinch: 'py-3 px-4 focus:border-[#311C77] focus:ring-[#311C77]',
          bmw: 'py-3 px-4 focus:border-fontPrimary focus:ring-fontPrimary',
          toyota: 'py-3 px-4 focus:border-fontPrimary focus:ring-fontPrimary',
          mopar: 'py-3 px-4',
          jeep: 'py-3 px-4',
          mini: 'py-3 px-4 focus:border-fontPrimary focus:ring-fontPrimary',
          jlr: 'focus:border-fontPrimary focus:ring-0 !font-widget-primary',
          common: `focus:border-fontPrimary focus:ring-0 ${componentStyles.value?.input || ''}`,
          referral: 'focus:border-fontPrimary focus:ring-0 !font-widget-primary',
          jaguar: 'p-5 focus:border-fontPrimary focus:ring-0',
          landrover: 'p-5 focus:border-fontPrimary focus:ring-0',
          volvo: 'px-5 py-4',
          crashdaddy: 'px-5 py-4',
        },
      },
      defaultVariants: {
        variant: getClientTheme(),
      },
    }
  );

  type InputProps = VariantProps<typeof inputVariants>;

  const props = withDefaults(
    defineProps<{
      modelValue?: T | string;
      value?: T | string;
      label?: string;
      placeholder?: string;
      prefix?: string;
      postfix?: string;
      name?: string;
      type?: string;
      autofocus?: boolean;
      autocomplete?: string;
      readonly?: boolean;
      clearable?: boolean;
      error?: string;
      variant?: InputProps['variant'];
      subtitle?: boolean;
      id?: string;
    }>(),
    {
      modelValue: '',
      value: '',
      label: '',
      placeholder: '',
      prefix: '',
      postfix: '',
      name: '',
      type: 'text',
      autofocus: false,
      autocomplete: undefined,
      readonly: false,
      clearable: false,
      subtitle: false,
      error: '',
      variant: undefined,
      id: '',
    }
  );

  const emit = defineEmits<{
    (event: 'update:modelValue', value: T | string): void;
    (event: 'clear'): void;
  }>();

  const input = ref<HTMLInputElement | null>(null);
  const inputValue = computed({
    get: () => props.modelValue ?? props.value,
    set: (val) => {
      emit('update:modelValue', val);
    },
  });
  const error = computed(() => props.error);
  const isInputFocused = ref(false);

  function clearInput() {
    inputValue.value = '';
    emit('clear');
  }

  defineExpose({ focus: () => input.value?.focus(), blur: () => input.value?.blur() });
</script>
